import { type AxiosInstance, type AxiosError, HttpStatusCode } from "axios";
import useSWR, { type KeyedMutator } from "swr";
import type { GetInheritanceReusePrefecturesAPIResponse } from "@/features/inheritanceReuse/types";
import { SendToTrackingService } from "@/api/handlers/errors";

const ENDPOINT = "/inheritance-reuse/prefectures";

interface IResponse {
  data: GetInheritanceReusePrefecturesAPIResponse | undefined;
  isLoading: boolean;
  mutate: KeyedMutator<GetInheritanceReusePrefecturesAPIResponse>;
  error: AxiosError | undefined;
}

/**
 * 相続後リユース候補の都道府県別件数・最終同期日時を取得する。
 * フィルタの都道府県セレクト（件数表示）に用いる。
 * @param {AxiosInstance} apiClient JWT 認証付き apiClient
 * @returns {IResponse}
 */
export const GetInheritanceReusePrefecturesAPI = (
  apiClient: AxiosInstance
): IResponse => {
  const fetcher = async (
    url: string
  ): Promise<GetInheritanceReusePrefecturesAPIResponse> => {
    const { data } =
      await apiClient.get<GetInheritanceReusePrefecturesAPIResponse>(url);
    return data;
  };

  const { data, isLoading, mutate, error } = useSWR<
    GetInheritanceReusePrefecturesAPIResponse,
    AxiosError
  >(ENDPOINT, async (url: string) => await fetcher(url), {
    revalidateOnFocus: false,
  });

  if (
    error &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    SendToTrackingService(error);
    return { data: undefined, isLoading, mutate, error };
  }

  return { data, isLoading, mutate, error };
};
